import React, {useState, useEffect} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import { Drawer } from '@material-ui/core';
import { BackdropCircular, Breadcrumb } from 'components';
import {
  getAKWAnalyticsReportDataApi,
  getAKWAnalyticsWeeklyReportDataApi
} from 'api/aestheticKpiWarehouse';
import CustomToolbar from './CustomToolbar';
import FilterDrawer from './FilterDrawer';
import ChartSample from './analyticsGraphsPivots/RevenueAnnualComparison';
import BarChartSample from './analyticsGraphsPivots/BarCartSample';
import PieChartSample from './analyticsGraphsPivots/PieChartSample';
import { data, pieData } from './sampleData';
import { CTA } from './utils/helperData';
import TotalsKPI from './analyticsGraphsPivots/TotalsKPI';
import ComingSoonPage from './ComingSoonPage';
import './styles/layout.css';
import SalesAnnualComparisonByProvider from './analyticsGraphsPivots/SalesAnnualComparisonByProvider';
import SalesAnnualComparisonByWeek from './analyticsGraphsPivots/SalesAnnualComparisonByWeek';


//TODO: remove after finishing the development
const docsLink = 'https://recharts.org/en-US/examples';
const comingSoonPages = ['retailSales', 'businessLine', 'visits1', 'visits2'];

const Analytics: React.FC = () => {
  const dispatch = useDispatch();
  const [width, setWidth] = useState(window.innerWidth);
  const [openFilter, setOpenFilter] = useState(false);
  const [activeCTA, setActiveCTA] = React.useState(CTA[0].value);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filters, setFilters] = useState({
    start_date: '2021-01-01',
    source_id: 2, // ID of the service 'Nextech' = 2, ModMed = 3
  });

  const {
    analyticsReportData,
    analyticsReportWeeklyData,
    isAnalyticsReportDataLoading
  } = useSelector((state: IReduxState) => state.aestheticKpiWarehouse);

  useEffect(() => {
    //debounce the resize event to prevent resizeObserver from firing too many times
    const handleResize = debounce(() => {
      setWidth(window.innerWidth);
    }, 300);

    window.addEventListener('resize', handleResize);

    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (activeCTA === 'revenueM') {
      getAKWAnalyticsReportDataApi({dispatch, filters});
    }

    if (activeCTA === 'revenueW') {
      getAKWAnalyticsWeeklyReportDataApi({dispatch, filters});
    }
  }, [activeCTA, filters]);

  const graphWidth = () => {
    if (width < 768) {
      return width - 110;
    } else if (width < 1280) {
      return (width - 110) / 2;
    } else if (width >= 1280) {
      return (width - 370) / 2;
    }

    return width / 2.5;
  };

  return (
    <div className="flex-grow h-full scroll-y">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Data Analytics Reports' }
          ]}
        />
        <Link to={docsLink} target="blank" className="px-10 pt-4 link">Official charts documentation examples</Link>
      </div>
      <div className="grid-container-full mx-8">
        <CustomToolbar
          setOpenFilter={setOpenFilter}
          activeCTA={activeCTA}
          setActiveCTA={setActiveCTA}
        />
      </div>
      {analyticsReportData && activeCTA === 'revenueM' &&
      <>
        <div className="grid-container-full mx-6">
          <TotalsKPI data={analyticsReportData} />
        </div>
        <div className="grid-container mx-8">
          <ChartSample width={graphWidth()} data={analyticsReportData} />
          {analyticsReportData.annual_sales_per_provider &&
          <SalesAnnualComparisonByProvider
            tableData={analyticsReportData.annual_sales_per_provider}
          />
          }
          <PieChartSample width={graphWidth()} data={pieData} />
          <BarChartSample width={graphWidth()} data={data} />
        </div>
      </>
      }
      {activeCTA === 'revenueW' &&
        <div className="grid-container mx-8">
          <SalesAnnualComparisonByWeek tableData={analyticsReportWeeklyData} />
        </div>
      }
      {comingSoonPages.includes(activeCTA) && <ComingSoonPage />}
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <FilterDrawer
          setOpenFilter={setOpenFilter}
        />
      </Drawer>
      <BackdropCircular loading={isAnalyticsReportDataLoading} title="Generating Report Data..." />
    </div>
  );
};

export default Analytics;
